import { CAMPAIGN_CARD_DIAL_SIZE } from "@/components/assemblies/campaignCard/constants";
import type { RubyCampaignId } from "@/services/backend/RubyData";
import { currencyFormatter } from "../../../../../utilities/currency";
import { eventNameToLabel } from "../../../../../utilities/events";
import { safeDivide } from "../../../../../utilities/number";
import { aggregateAsyncData } from "../../../../../utilities/requests";
import { languageString } from "../../../../../utilities/text";
import { isChannelWithKeywords } from "../../../../../utilities/types";
import { useCampaignDataLoader } from "../../../../assemblies/campaignDataLoader/CampaignDataLoader";
import RequestLoader from "../../../../blocks/requestLoader/RequestLoader";
import StatRadialTarget from "../../../../blocks/stats/radialTarget/StatRadialTarget";

interface CostPerEventDialProps {
    campaignId?: RubyCampaignId;
    size?: number;
}

export default function CostPerEventDial({ size = CAMPAIGN_CARD_DIAL_SIZE, campaignId }: CostPerEventDialProps) {
    const loader = useCampaignDataLoader(campaignId);
    const channel = loader.useChannel();
    const campaign = loader.useCampaign();
    const currency = loader.useCurrency();
    const metrics = loader.useMetricsSummary();

    let targetEvent: string = null;
    if (isChannelWithKeywords(campaign.data?.channelType, channel.data)) {
        targetEvent = channel.data.targetEvent;
    }

    const eventData = loader.useEventSummary(targetEvent);

    const formatCurrency = currencyFormatter(currency);

    const costPerEvent = safeDivide(metrics?.data?.spend, eventData.data?.events);
    const cpeTarget = isChannelWithKeywords(campaign.data?.channelType, channel.data)
        ? channel.data?.targetEventGoal
        : 0;

    return (
        <div className="u-relative">
            <RequestLoader
                request={aggregateAsyncData(eventData, campaign, channel, metrics)}
                placeholder={
                    <StatRadialTarget
                        label={languageString("campaign.listing.cpeTarget.title")}
                        value={0}
                        target={0}
                        formatter={formatCurrency}
                        negativeIsGood
                        size={size}
                    />
                }
                fallback={
                    <div className="u-fade2">
                        <StatRadialTarget
                            label={languageString("campaign.listing.cpeTarget.title")}
                            value={0}
                            target={0}
                            formatter={formatCurrency}
                            negativeIsGood
                            size={size}
                        />
                    </div>
                }
            >
                <StatRadialTarget
                    label={languageString("campaign.listing.cpeTarget.title")}
                    hint={languageString("campaign.listing.cpeTarget.description")}
                    value={costPerEvent ?? 0}
                    target={cpeTarget}
                    formatter={formatCurrency}
                    negativeIsGood
                    size={size}
                    subLabel={languageString("campaign.listing.cpeTarget.target", "", [
                        eventNameToLabel(targetEvent),
                        formatCurrency(cpeTarget),
                    ])}
                />
            </RequestLoader>
        </div>
    );
}
